import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
// import { useUser } from "../../context/userContext";
import { ReactComponent as CloseIcon } from "../../assets/icons/chevron_icon.svg";
import { ReactComponent as InfoIcon } from "../../assets/icons/info_icon.svg";
import { ReactComponent as ForgotPasswordIcon } from "../../assets/icons/forgot_password_icon.svg";
import { ReactComponent as MailIcon } from "../../assets/icons/mail_icon.svg";
import { ReactComponent as ResetPasswordIcon } from "../../assets/icons/new_password_icon.svg";

function ForgotPasswordModal({ onClose }) {
  // const { userData } = useUser();

  const [isContentLoading, setIsContentLoading] = useState(false);

  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState(false);

  const inputs = useRef([]);
  const [otp, setOtp] = useState("");
  const [otpError, setOtpError] = useState(false);
  const [resendActive, setResendActive] = useState(true);
  const [timer, setTimer] = useState(60);
  const [resendCount, setResendCount] = useState(0);

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordError, setPasswordError] = useState(false);

  const handleForgotPassword = async () => {
    // Email validation using regex
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!emailRegex.test(email)) {
      setEmailError(true);
      return;
    }

    // Clear any previous email error
    setEmailError(false);

    try {
      setIsContentLoading(true);

      const response = await axios.post("https://soliboat.solidaridadasia.com/forgot_password", {
        email: email,
      });
      // console.log(response)
      if (response?.status === 200) {
        setStep(2);
      } else {
        setEmailError(true);
      }
    } catch (error) {
      setEmailError(true);
      console.error("Error sending OTP:", error);
    } finally {
      setIsContentLoading(false);
    }
  };

  const handleInput = (index, event) => {
    const value = event.target.value;

    // Update the otp state with the new digit
    setOtp((prevOtp) => {
      const updatedOtp = [...prevOtp];
      updatedOtp[index] = value;
      return updatedOtp.join(""); // Combine the array of digits into a string
    });

    // Move focus to the next input field if available
    const nextInput = inputs.current[index + 1];
    if (nextInput && value) {
      nextInput.focus();
    }
  };

  const handlePaste = (event) => {
    event.preventDefault();
    const pasteData = event.clipboardData.getData("text/plain");
    const pasteDigits = pasteData.split("").filter((char) => !isNaN(char));

    inputs.current.forEach((input, index) => {
      if (pasteDigits[index]) {
        input.value = pasteDigits[index];
        handleInput(index, { target: { value: pasteDigits[index] } });
      }
    });
  };

  const handleResendCode = () => {
    if (resendCount < 2) {
      setResendActive(false);
      setTimer(60);
      setResendCount(resendCount + 1);

      handleForgotPassword();
      setTimeout(() => {
        setResendActive(true);
      }, 60000);
    }
  };

  useEffect(() => {
    let interval;
    if (step === 2 && resendActive && timer > 0) {
      interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [step, resendActive, timer]);

  const handleValidateOTP = async () => {
    try {
      setIsContentLoading(true);

      await axios.post(
        "https://soliboat.solidaridadasia.com/validate_forgot_password_otp",
        {
          email: email,
          otp: otp,
        }
      );
      setStep(3);
    } catch (error) {
      setOtpError(true);
      console.error("Error validating OTP:", error);
    } finally {
      setIsContentLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (newPassword !== confirmPassword) {
      setPasswordError(true);
      setNewPassword("");
      setConfirmPassword("");
      return;
    }

    try {
      setIsContentLoading(true);

      await axios.post("https://soliboat.solidaridadasia.com/update_password", {
        email: email,
        password: newPassword,
      });
      setEmail("");
      setOtp("");
      setNewPassword("");
      setConfirmPassword("");
      onClose();
      setStep(1);
    } catch (error) {
      console.error("Error updating password:", error);
    } finally {
      setIsContentLoading(false);
    }
  };

  return (
    <>
      {/* Loading screen while API calls are in progress */}
      {isContentLoading && (
        <div className="fixed inset-0 flex flex-col items-center justify-center bg-blue-100/70 z-[100]">
          <div className="w-20 h-20 animate-spin">
            <div className="h-full w-full border-4 border-t-dark/50 border-b-dark/80 rounded-[50%]" />
          </div>
        </div>
      )}
      <div className="fixed inset-0 flex items-center justify-center drop-shadow-[0_4px_24px_rgba(0,0,0,0.24)]">
        <div className="fixed inset-0 bg-black opacity-50" />
        <div className="relative flex flex-col justify-center items-center p-4 w-[90%] md:w-2/3 lg:w-2/4 xl:w-1/3 bg-white rounded-xl">
          {step === 1 && (
            <div className="w-full flex flex-col justify-center items-center">
              <div
                className="absolute top-3 md:top-6 left-3 md:left-4 flex items-center cursor-pointer"
                onClick={() => onClose(false)}
              >
                <CloseIcon className="w-6 h-6 -rotate-90" />
                <p className="ml-1 text-sm md:text-normal">Back</p>
              </div>
              <h2 className="mt-8 mb-6 text-gray-900 text-xl md:text-2xl font-medium">
                Forgot Password?
              </h2>
              <ForgotPasswordIcon className="w-36 h-36 md:w-48 md:h-48 p-2 bg-blue-500/10 rounded-full" />
              <div className="w-full flex flex-col justify-center items-center mb-4 md:mb-8">
                <p className="my-6 text-center text-sm md:text-base text-neutral-700">
                  Please enter you registered email address <br /> to receive a
                  verification code.
                </p>
                <div className="w-[90%] md:w-4/5 mt-2 md:mt-4 mb-2 md:mb-4">
                  <label
                    htmlFor="email"
                    className="block text-xs font-medium text-neutral-500"
                  >
                    Email Address
                  </label>
                  <input
                    type="email"
                    name="email"
                    placeholder="Enter your registered email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full border-b border-neutral-200 py-2 text-base text-body-color placeholder-body-color outline-none focus:border-primary placeholder:text-sm"
                  />
                  {emailError && (
                    <div className="flex items-center mt-2 md:mt-3">
                      <InfoIcon className="w-6 h-6" />
                      <span className="text-red text-sm ml-2">
                        Please enter your valid registered email.
                      </span>
                    </div>
                  )}
                </div>
                <button
                  className="flex justify-center items-center w-3/5 bg-primary text-white mt-2 md:mt-4 px-2 py-2 rounded"
                  onClick={handleForgotPassword}
                >
                  Send Code
                </button>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="w-full flex flex-col justify-center items-center">
              <div
                className="absolute top-3 md:top-6 left-3 md:left-4 flex items-center cursor-pointer"
                onClick={() => onClose(false)}
              >
                <CloseIcon className="w-6 h-6 -rotate-90" />
                <p className="ml-1 text-sm md:text-normal">Back</p>
              </div>
              <h2 className="mt-8 mb-6 text-gray-900 text-xl md:text-2xl font-medium">
                Verify Your Email
              </h2>
              <MailIcon className="w-36 h-36 md:w-48 md:h-48" />
              <div className="w-full flex flex-col justify-center items-center mb-4 md:mb-8">
                <p className="mb-4 text-center text-sm md:text-base text-neutral-700">
                  Please enter the 6 digit code sent to your <br /> registered
                  email.
                </p>
                <div className="w-[90%] md:w-4/5 mt-2 mb-6">
                  <div class="flex justify-center items-center space-x-4">
                    {[...Array(6)].map((_, index) => (
                      <input
                        key={index}
                        ref={(el) => (inputs.current[index] = el)}
                        type="text"
                        maxLength="1"
                        className="w-8 h-8 md:w-10 md:h-10 border border-gray-300 rounded text-center text-xl focus:outline-none focus:border-blue-500"
                        onInput={(e) => handleInput(index, e)}
                        onPaste={handlePaste}
                      />
                    ))}
                  </div>

                  {otpError && (
                    <div className="flex items-center mt-2 md:mt-3">
                      <InfoIcon className="w-6 h-6" />
                      <span className="text-red text-sm ml-2">
                        Please enter correct OTP.
                      </span>
                    </div>
                  )}

                  <p className="mt-4 text-center text-sm text-neutral-700">
                    The OTP expires in{" "}
                    <span className="font-semibold">5 minutes</span>.
                  </p>
                </div>
                <button
                  onClick={handleResendCode}
                  disabled={(resendActive && timer > 0) || resendCount >= 2}
                  className={`text-sm font-medium underline ${timer > 0 || resendCount >= 2
                      ? "text-gray-500 cursor-not-allowed"
                      : "text-primary"
                    }`}
                >
                  {resendActive && resendCount < 2
                    ? `Resend Code${timer > 0 ? ` (in ${timer} seconds).` : "?"
                    }`
                    : "Resend Code (Limit Exceeded)"}
                </button>
                <button
                  className="flex justify-center items-center w-3/5 bg-primary text-white mt-2 md:mt-4 px-2 py-2 rounded"
                  onClick={handleValidateOTP}
                >
                  Verify Code
                </button>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="w-full flex flex-col justify-center items-center">
              <div
                className="absolute top-3 md:top-6 left-3 md:left-4 flex items-center cursor-pointer"
                onClick={() => onClose(false)}
              >
                <CloseIcon className="w-6 h-6 -rotate-90" />
                <p className="ml-1 text-sm md:text-normal">Back</p>
              </div>
              <h2 className="mt-8 mb-6 text-gray-900 text-xl md:text-2xl font-medium">
                Create New Password
              </h2>
              <ResetPasswordIcon className="w-36 h-36 md:w-48 md:h-48" />
              <div className="w-full flex flex-col justify-center items-center mb-4 md:mb-8">
                <p className="my-6 text-center text-sm md:text-base text-neutral-700">
                  Your new password must be different
                  <br /> from previously used password.
                </p>
                <div className="w-[90%] md:w-4/5 mt-2 md:mt-4 mb-2 md:mb-4">
                  <label
                    htmlFor="password"
                    className="block text-xs font-medium text-neutral-500"
                  >
                    New Password
                  </label>
                  <input
                    type="password"
                    name="password"
                    placeholder="Enter a password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="w-full border-b border-neutral-200 py-2 mb-4 text-base text-body-color placeholder-body-color outline-none focus:border-primary placeholder:text-sm"
                  />
                  <label
                    htmlFor="password"
                    className="block text-xs font-medium text-neutral-500"
                  >
                    Confirm Password
                  </label>
                  <input
                    type="password"
                    name="password"
                    placeholder="Confirm password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full border-b border-neutral-200 py-2 text-base text-body-color placeholder-body-color outline-none focus:border-primary placeholder:text-sm"
                  />
                  {passwordError && (
                    <div className="flex items-center mt-2 md:mt-3">
                      <InfoIcon className="w-6 h-6" />
                      <span className="text-red text-sm ml-2">
                        New password and confirm password do not match.
                      </span>
                    </div>
                  )}
                </div>
                <button
                  className="flex justify-center items-center w-3/5 bg-primary text-white mt-2 md:mt-4 px-2 py-2 rounded"
                  onClick={handleResetPassword}
                >
                  Change Password
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default ForgotPasswordModal;
